import React from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AgenticAISchoolManagementProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AgenticAISchoolManagement: React.FC<AgenticAISchoolManagementProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  const aiFeatures = [
    { icon: 'bi-robot', title: 'AI-Powered Chatbot', desc: 'Answer parent queries 24/7 with intelligent conversational AI', color: '#5C2597' },
    { icon: 'bi-lightbulb', title: 'Smart Recommendations', desc: 'AI suggests actions based on student performance patterns', color: '#2D7DD2' },
    { icon: 'bi-graph-up', title: 'Predictive Analytics', desc: 'Forecast attendance, fee defaults, and academic outcomes', color: '#28A745' },
    { icon: 'bi-translate', title: 'Auto Translation', desc: 'Communicate with parents in their preferred language', color: '#FFC107' },
    { icon: 'bi-file-earmark-text', title: 'Smart Document Processing', desc: 'Auto-extract data from uploaded documents', color: '#E91E63' },
    { icon: 'bi-bell-fill', title: 'Intelligent Alerts', desc: 'Proactive notifications based on contextual triggers', color: '#00BCD4' },
  ];

  const useCases = [
    { title: 'Automated Parent Communication', desc: 'AI drafts personalized messages for attendance, fees, and events' },
    { title: 'Student Performance Insights', desc: 'Identify at-risk students before issues escalate' },
    { title: 'Smart Scheduling', desc: 'AI optimizes timetables based on constraints and preferences' },
    { title: 'Fee Collection Optimization', desc: 'Predict defaulters and send targeted reminders' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 position-relative">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-primary mb-3 px-3 py-2">
                <i className="bi bi-stars me-2"></i>Next-Gen Technology
              </span>
              <h1 className="display-4 fw-bold mb-4">
                Agentic AI for School Management
              </h1>
              <p className="lead mb-4 opacity-90">
                Go beyond automation with Agentic AI that thinks, plans, and acts.
                Our AI agents handle complex tasks autonomously, freeing your staff
                to focus on what matters most - education.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>See AI in Action
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Try Demo
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white bg-opacity-10 rounded-4 p-4 backdrop-blur">
                <div className="text-start">
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center me-3"
                      style={{ width: '40px', height: '40px' }}>
                      <i className="bi bi-robot"></i>
                    </div>
                    <div>
                      <strong>AI Assistant</strong>
                      <p className="small mb-0 opacity-75">Active now</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded p-3 mb-2">
                    <p className="mb-0 small">"I've identified 12 students with declining attendance this month. Would you like me to draft messages to their parents?"</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded p-3 mb-2">
                    <p className="mb-0 small">"Fee reminders sent to 45 parents. 23 have already viewed the message."</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded p-3">
                    <p className="mb-0 small">"Tomorrow's timetable optimized. 2 conflicts resolved automatically."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Agentic AI */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-4">What is Agentic AI?</h2>
              <p className="text-muted mb-4">
                Unlike traditional automation that follows fixed rules, Agentic AI can understand context,
                make decisions, and take actions autonomously. It's like having a team of intelligent
                assistants working 24/7 for your school.
              </p>
              <div className="row g-3">
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <span>Understands Context</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <span>Makes Decisions</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <span>Takes Actions</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <span>Learns & Improves</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bg-white rounded-4 p-4 shadow">
                <h5 className="mb-4">Traditional Automation vs Agentic AI</h5>
                <table className="table table-borderless mb-0">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="text-muted">Traditional</th>
                      <th className="text-success">Agentic AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Decision Making</td>
                      <td><i className="bi bi-x-circle text-danger"></i></td>
                      <td><i className="bi bi-check-circle-fill text-success"></i></td>
                    </tr>
                    <tr>
                      <td>Context Understanding</td>
                      <td><i className="bi bi-x-circle text-danger"></i></td>
                      <td><i className="bi bi-check-circle-fill text-success"></i></td>
                    </tr>
                    <tr>
                      <td>Autonomous Actions</td>
                      <td><i className="bi bi-x-circle text-danger"></i></td>
                      <td><i className="bi bi-check-circle-fill text-success"></i></td>
                    </tr>
                    <tr>
                      <td>Continuous Learning</td>
                      <td><i className="bi bi-x-circle text-danger"></i></td>
                      <td><i className="bi bi-check-circle-fill text-success"></i></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">AI-Powered Features</h2>
            <p className="text-muted lead">Intelligence built into every aspect of school management</p>
          </div>
          <div className="row g-4">
            {aiFeatures.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Real-World AI Use Cases</h2>
          </div>
          <div className="row g-4">
            {useCases.map((useCase, idx) => (
              <div key={idx} className="col-md-6">
                <div className="bg-white rounded-4 p-4 shadow-sm h-100">
                  <div className="d-flex align-items-start">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                      style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                      <i className="bi bi-lightning-charge"></i>
                    </div>
                    <div>
                      <h5 className="mb-2">{useCase.title}</h5>
                      <p className="text-muted mb-0">{useCase.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Experience the Future of School Management</h2>
          <p className="lead opacity-75 mb-4">Be among the first schools to leverage Agentic AI</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-rocket-takeoff me-2"></i>Request Early Access
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AgenticAISchoolManagement;
